import React, { useState, useEffect, useMemo, useRef } from "react";
import Illustration1 from "../Assets/Frame1.png";
import Illustration2 from "../Assets/Frame2.png";
import Illustration3 from "../Assets/Frame3.png";
import Illustration4 from "../Assets/Frame4.png";
import Illustration5 from "../Assets/Frame5.png";
import Illustration6 from "../Assets/Frame6.png";
import Illustration1Ar from "../Assets/Frame1(AR).png";
import Illustration2Ar from "../Assets/Frame2(AR).png";
import Illustration3Ar from "../Assets/Frame3(AR).png";
import Illustration4Ar from "../Assets/Frame4(AR).png";
import Illustration5Ar from "../Assets/Frame5(AR).png";
import Illustration6Ar from "../Assets/Frame6(AR).png";
import "./App.css";
import { HiCheck } from "react-icons/hi";
import { useTranslation } from "react-i18next";
import JourneyMap from "./JourneyMap";

const Scroll2 = () => {
  const { t, i18n } = useTranslation();

  const steps = useMemo(() => {
    const isAr = i18n.language === "ar";
    const images = isAr
      ? [
          Illustration1Ar,
          Illustration2Ar,
          Illustration3Ar,
          Illustration4Ar,
          Illustration5Ar,
          Illustration6Ar,
        ]
      : [
          Illustration1,
          Illustration2,
          Illustration3,
          Illustration4,
          Illustration5,
          Illustration6,
        ];
    return images.map((image, i) => ({
      image,
      title: t(`serviceInfo[${i}].title`),
      text: t(`serviceInfo[${i}].text`),
      tags: [t(`serviceInfo[${i}].tag1`), t(`serviceInfo[${i}].tag2`)],
    }));
  }, [t, i18n.language]);

  const [activeIndex, setActiveIndex] = useState(0);
  const slotRefs = useRef([]);
  const mapColRef = useRef(null);
  const setSlotRef = (i) => (el) => {
    slotRefs.current[i] = el;
  };

  // On mobile the map is pinned at the top; hide each step card once it has
  // scrolled fully behind the map so nothing peeks above it.
  useEffect(() => {
    const mql = window.matchMedia("(max-width: 900px)");
    let raf = null;

    const apply = () => {
      raf = null;
      const mapEl = mapColRef.current;
      if (!mapEl) return;
      const isMobile = mql.matches;
      const mapBottom = mapEl.getBoundingClientRect().bottom;
      slotRefs.current.forEach((slot) => {
        const card = slot && slot.querySelector(".hiw-card");
        if (!card) return;
        const behind =
          isMobile && card.getBoundingClientRect().bottom <= mapBottom;
        card.classList.toggle("is-behind", behind);
      });
    };

    const onScroll = () => {
      if (raf == null) raf = requestAnimationFrame(apply);
    };

    apply();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (raf != null) cancelAnimationFrame(raf);
    };
  }, [steps.length]);

  // The slot crossing the vertical centre of the viewport becomes active.
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveIndex(Number(entry.target.dataset.index));
          }
        });
      },
      { rootMargin: "-50% 0px -50% 0px", threshold: 0 }
    );

    slotRefs.current.forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, [steps.length]);

  return (
    <section className="hiw">
      <div className="hiw-header">
        <span className="hiw-eyebrow">
          <span className="dot" />
          {t("hiw_eyebrow")}
        </span>
        <h2 className="hiw-title">{t("hiw_title")}</h2>
        <p className="hiw-subtitle">{t("hiw_subtitle")}</p>
      </div>

      <div className="hiw-body">
        <div className="hiw-cards">
          {steps.map((step, index) => (
            <div
              key={index}
              className="hiw-slot"
              data-index={index}
              ref={setSlotRef(index)}
            >
              <div
                className={`scroll-card hiw-card ${
                  activeIndex === index ? "is-active" : "is-dim"
                }`}
              >
                <div className="scroll-card-badge">
                  {String(index + 1).padStart(2, "0")}
                </div>
                <h3 className="scroll-card-title">{step.title}</h3>
                <p className="scroll-card-text">{step.text}</p>
                <div className="scroll-card-divider" />
                <div className="scroll-card-tags">
                  {step.tags.map((tag, ti) => (
                    <span className="scroll-card-tag" key={ti}>
                      <HiCheck />
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="hiw-map-col" ref={mapColRef}>
          <div className="hiw-map-sticky">
            <JourneyMap steps={steps} activeIndex={activeIndex} />
          </div>
        </div>
      </div>
    </section>
  );
};

export default Scroll2;
